import { Activity, AlertTriangle, Cloud, Eye, Key, Lock, Shield } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import AuthButton from './components/AuthButton';
import './App.css';

// Particle System Component
const ParticleCanvas = ({ mode = 'particles' }) => {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animationRef = useRef();
  const mousePosRef = useRef({ x: 0, y: 0 });

  const createParticle = useCallback((x, y, canvas) => ({
    x: x || Math.random() * canvas.width,
    y: y || Math.random() * canvas.height,
    vx: (Math.random() - 0.5) * 2,
    vy: (Math.random() - 0.5) * 2,
    size: Math.random() * 3 + 1,
    life: 1,
    decay: Math.random() * 0.01 + 0.005,
    color: `hsl(${180 + Math.random() * 60}, 70%, 60%)`,
  }), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize particles
    for (let i = 0; i < 30; i++) {
      particlesRef.current.push(createParticle(null, null, canvas));
    }

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mousePosRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    canvas.addEventListener('mousemove', handleMouseMove);

    const animate = () => {
      ctx.fillStyle = 'rgba(10, 10, 10, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw particles
      for (let i = particlesRef.current.length - 1; i >= 0; i--) {
        const p = particlesRef.current[i];

        p.x += p.vx;
        p.y += p.vy;
        p.life -= p.decay;

        const dx = mousePosRef.current.x - p.x;
        const dy = mousePosRef.current.y - p.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 100) {
          p.vx += dx * 0.0001;
          p.vy += dy * 0.0001;
        }

        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.save();
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        if (p.life <= 0) {
          particlesRef.current.splice(i, 1);
        }
      }

      while (particlesRef.current.length < 20) {
        particlesRef.current.push(createParticle(null, null, canvas));
      }

      if (mode === 'network') {
        ctx.strokeStyle = 'rgba(0, 212, 255, 0.3)';
        ctx.lineWidth = 1;

        for (let i = 0; i < particlesRef.current.length; i++) {
          for (let j = i + 1; j < particlesRef.current.length; j++) {
            const dx = particlesRef.current[i].x - particlesRef.current[j].x;
            const dy = particlesRef.current[i].y - particlesRef.current[j].y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 150) {
              ctx.globalAlpha = (150 - distance) / 150 * 0.5;
              ctx.beginPath();
              ctx.moveTo(particlesRef.current[i].x, particlesRef.current[i].y);
              ctx.lineTo(particlesRef.current[j].x, particlesRef.current[j].y);
              ctx.stroke();
            }
          }
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [mode, createParticle]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />;
};

// Security Module Card Component
const SecurityModule = ({ icon: Icon, title, status, metrics, onClick, isActive }) => (
  <div
    onClick={onClick}
    className={`relative p-6 rounded-xl backdrop-blur-md border transition-all cursor-pointer transform hover:scale-105 ${isActive
      ? 'bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border-cyan-400/50'
      : 'bg-white/5 border-white/10 hover:bg-white/10'
      // eslint-disable-next-line indent
      }`}
  >
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <Icon className={`w-6 h-6 ${status === 'secure' ? 'text-green-400' : status === 'warning' ? 'text-yellow-400' : 'text-red-400'}`} />
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${status === 'secure' ? 'bg-green-400/20 text-green-400' :
        status === 'warning' ? 'bg-yellow-400/20 text-yellow-400' :
          'bg-red-400/20 text-red-400'
        // eslint-disable-next-line indent
        }`}>
        {status.toUpperCase()}
      </span>
    </div>
    {metrics && (
      <div className="space-y-2">
        {Object.entries(metrics).map(([key, value]) => (
          <div key={key} className="flex justify-between text-sm">
            <span className="text-gray-400">{key}:</span>
            <span className="text-white font-medium">{value}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

// Main App Component
const CyberDogSecurityDashboard = () => {
  const [activeModule, setActiveModule] = useState(null);
  const [visualMode, setVisualMode] = useState('network');
  const [stats, setStats] = useState({
    threats: 0,
    scans: 247,
    certificates: 12,
    uptime: '99.98%',
  });

  const [modules] = useState([
    {
      id: 'certificates',
      icon: Key,
      title: 'SSL/TLS Certificates',
      status: 'secure',
      metrics: { 'Valid Certs': 12, 'Expiring Soon': 2, 'Auto-Renew': 'Active' },
    },
    {
      id: 'sanitization',
      icon: Shield,
      title: 'Input Sanitization',
      status: 'secure',
      metrics: { 'Blocked Attempts': 156, 'XSS Prevention': 'Active', 'SQL Injection': 'Protected' },
    },
    {
      id: 'audit',
      icon: Eye,
      title: 'Compliance Audit',
      status: 'warning',
      metrics: { 'GDPR': 'Compliant', 'ISO 27001': 'Pending', 'Last Audit': '2 days ago' },
    },
    {
      id: 'encryption',
      icon: Lock,
      title: 'Data Encryption',
      status: 'secure',
      metrics: { 'Encrypted Records': '100%', 'Algorithm': 'AES-256', 'Key Rotation': 'Daily' },
    },
    {
      id: 'cloudflare',
      icon: Cloud,
      title: 'CDN & DDoS Protection',
      status: 'secure',
      metrics: { 'Requests Filtered': '1.2M', 'Threats Blocked': 342, 'Cache Hit Rate': '94%' },
    },
    {
      id: 'monitoring',
      icon: Activity,
      title: 'Real-time Monitoring',
      status: 'secure',
      metrics: { 'Active Monitors': 47, 'Avg Response': '142ms', 'Alerts Today': 3 },
    },
  ]);

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        ...prev,
        threats: prev.threats + Math.floor(Math.random() * 2),
        scans: prev.scans + Math.floor(Math.random() * 5) + 1,
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 relative overflow-hidden">
      {/* Background Canvas */}
      <ParticleCanvas mode={visualMode} />

      {/* Main Content */}
      <div className="relative z-10 p-8">
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent flex items-center gap-3">
                <Shield className="w-10 h-10 text-cyan-400" />
                CyberDog Security Suite
              </h1>
              <p className="text-gray-400 mt-2">Advanced HubSpot Security & Compliance Platform</p>
            </div>
            <div className="flex flex-col items-end gap-3">
              <AuthButton />
              <button
                onClick={() => setVisualMode(visualMode === 'particles' ? 'network' : 'particles')}
                className="px-4 py-2 bg-white/10 backdrop-blur-md rounded-lg border border-white/20 text-white hover:bg-white/20 transition-all"
              >
                Toggle Visualization
              </button>
            </div>
          </div>
        </header>

        {/* Real-time Stats Bar */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {Object.entries(stats).map(([key, value]) => (
            <div key={key} className="bg-black/30 backdrop-blur-md rounded-lg p-4 border border-white/10">
              <p className="text-gray-400 text-sm capitalize">{key}</p>
              <p className="text-2xl font-bold text-white">{value}</p>
            </div>
          ))}
        </div>

        {/* Security Modules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {modules.map(module => (
            <SecurityModule
              key={module.id}
              {...module}
              isActive={activeModule === module.id}
              onClick={() => setActiveModule(activeModule === module.id ? null : module.id)}
            />
          ))}
        </div>

        {/* Action Panel */}
        <div className="bg-black/30 backdrop-blur-md rounded-xl p-6 border border-white/10">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            Quick Actions
          </h2>
          <div className="flex gap-4 flex-wrap">
            <button className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all">
              Run Security Scan
            </button>
            <button className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all">
              Generate Compliance Report
            </button>
            <button className="px-4 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg hover:from-green-600 hover:to-teal-600 transition-all">
              Update All Certificates
            </button>
            <button className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all border border-white/20">
              View Audit Logs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CyberDogSecurityDashboard;